import React, { FC, MouseEvent } from 'react'
import isEmpty from 'lodash/isEmpty'
import { ProductProvider, useProductContext } from './context'
import ProductImage from './ProductImage'
import ProductName from './ProductName'
import Numeric, { NumericProps } from './Numeric'
import Price, { PriceProps } from './Price/Price'
import { getChildrenByType } from './utils'
import ProductOptions, { ProductOptionsProps } from './Options'
import ProductServiceOptions, {
  ProductServiceOptionsProps,
} from './ServiceOptions'
import ProductFooter, { ProductFooterProps } from './ProductFooter'
import Card from '@/checkout-instore-legacy/components/Commons/Card'

export interface CustomPriceProps {
  quantity: number
  price: number
}

/**
 *   This entity exists because the actual type shape does not support data
 * to keep info about promotional items.
 *   Today, in case of promotional items, the API respond with more than one
 * product to represent this scenario but our UX not attend this and beucause
 * that we must merge this products to create a in single representation
 */
export type ItemProductCart = Item & {
  type?: string
  prices?: CustomPriceProps[]
}

export type BorderType = 'danger' | 'warning' | 'basic' | 'default'
export const BorderType = {
  danger: 'danger' as BorderType,
  warning: 'warning' as BorderType,
  basic: 'basic' as BorderType,
  default: 'default' as BorderType,
}

interface ProductSummaryProps {
  item: ItemProductCart
  borderType?: BorderType
  onClick?: (event: React.MouseEvent) => void
  className?: string
}

interface ProductNameContainerProps {
  children?: React.ReactElement
  className?: string
}

export interface Composites {
  NumericStepper: FC<NumericProps>
  Price: FC<PriceProps>
  Name: FC<ProductNameContainerProps>
  Content: FC
  Options: FC<ProductOptionsProps>
  ServiceOptions: FC<ProductServiceOptionsProps>
  Footer: FC<ProductFooterProps>
  ClickableArea: FC
  Image: FC
  Origin: FC
}

type Props = ProductSummaryProps

const ProductSummary: FC<Props> & Composites = ({
  item,
  children,
  borderType,
  onClick,
  className,
}) => {
  const {
    content,
    name,
    image,
    options,
    serviceOptions,
    custom,
    footer,
    origin,
  } = getChildrenByType(children)

  const renderProductImage = () => {
    if (isEmpty(image)) {
      return <ProductImage src={item.imageUrl} alt={item.name} />
    }
    return image
  }

  const customClass = className || 'ph5-s pt5-s pb5-ns pb3-s'

  return (
    <Card
      onClick={onClick}
      borderType={borderType}
      id="product-summary__card"
      cy="product-summary"
    >
      <div className={customClass}>
        <div className="flex ">
          <div className="mr5-ns mr4-s">
            <ProductProvider item={item}>
              {renderProductImage()}
            </ProductProvider>
          </div>
          <div className="flex flex-column flex-auto justify-between">
            <div className="flex justify-between w-100">
              <div className="flex justify-between flex-wrap w-100">
                <ProductProvider item={item}>{name}</ProductProvider>
                <ProductProvider item={item}>{serviceOptions}</ProductProvider>
                {origin}
              </div>
              <div className="product-summary__options mw-20">
                <ProductProvider item={item}>{options}</ProductProvider>
              </div>
            </div>
            <div className="flex justify-between">
              <ProductProvider item={item}>
                {content}
                {custom}
              </ProductProvider>
            </div>
          </div>
        </div>
        <div>{footer}</div>
      </div>
    </Card>
  )
}

const ProductNameContainer: FC<ProductNameContainerProps> = ({
  children,
  className,
}) => {
  const { item } = useProductContext()

  const finalChildren = children ? (
    React.cloneElement(children, { item })
  ) : (
    <ProductName item={item} />
  )

  return <div className={`flex flex-auto ${className}`}>{finalChildren}</div>
}

const ProductImageContainer: FC = ({ children }) => {
  const { item } = useProductContext()

  // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
  const finalChildren = children ? React.cloneElement(children, { item }) : null
  return finalChildren
}

ProductImageContainer.displayName = 'ProductImageContainer'

const ProductContent: FC = ({ children }) => {
  return (
    <div
      className={`flex flex-column-s flex-row-ns items-end-ns justify-between-ns w-100`}
    >
      {children}
    </div>
  )
}

export const ClickableArea: FC = ({ children }) => (
  <div onClick={(event: MouseEvent) => event.stopPropagation()}>{children}</div>
)

export const OriginContainer: FC = ({ children }) => (
  <div id="origin-container" className="flex flex-row-ns flex-column-s w-100">
    {children}
  </div>
)

ProductSummary.NumericStepper = Numeric
// TODO: this error popped up when moving the checkout-instore project
// to checkout-instore-legacy, but needs to be fixed at some point
// @ts-ignore
ProductSummary.Price = Price
ProductSummary.Name = ProductNameContainer
ProductSummary.Content = ProductContent
ProductSummary.Options = ProductOptions
ProductSummary.ServiceOptions = ProductServiceOptions
ProductSummary.ClickableArea = ClickableArea
ProductSummary.Footer = ProductFooter
ProductSummary.Image = ProductImageContainer
ProductSummary.Origin = OriginContainer

export default ProductSummary
